import React, { useState, useEffect } from 'react';
import { X, Send, Mail, Paperclip, Clock, AlertCircle, CheckCircle } from 'lucide-react';
import outlookApiService from '../services/outlookApiService';

const EmailPreviewModal = ({ 
  isOpen, 
  onClose, 
  briefContent, 
  tenantData, 
  calculationResult,
  adjustmentType 
}) => {
  const [emailData, setEmailData] = useState({
    to: '',
    cc: '',
    bcc: '',
    subject: '',
    body: '',
    priority: 'normal',
    sendTime: 'now'
  });
  
  const [sending, setSending] = useState(false);
  const [sendResult, setSendResult] = useState(null);
  const [showConfirmation, setShowConfirmation] = useState(false);

  // Initialize email data when modal opens
  useEffect(() => {
    if (isOpen && tenantData && briefContent) {
      const unitInfo = calculationResult?.unit?.unit_number || calculationResult?.unit?.identifier || 'Unbekannt';
      const propertyInfo = calculationResult?.property?.property_name || calculationResult?.property?.name || 'Unbekannt';
      
      setEmailData({
        to: tenantData.email || '',
        cc: '',
        bcc: '',
        subject: `Anpassung Ihrer Miete - ${propertyInfo}, Einheit ${unitInfo}`,
        body: convertBriefToEmailBody(briefContent),
        priority: 'normal',
        sendTime: 'now'
      });
    }
  }, [isOpen, tenantData, briefContent, calculationResult]);

  // Convert brief content to email-friendly format
  const convertBriefToEmailBody = (briefContent) => {
    if (!briefContent) return '';
    
    // Remove excessive line breaks and format for email
    return briefContent
      .replace(/\n\n\n+/g, '\n\n')
      .replace(/^\s+|\s+$/g, '')
      .trim();
  };

  const handleInputChange = (field, value) => {
    setEmailData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSendEmail = async () => {
    if (!emailData.to.trim()) {
      alert('Bitte geben Sie eine E-Mail-Adresse ein.');
      return;
    }

    setShowConfirmation(true);
  };

  const confirmSendEmail = async () => {
    setSending(true);
    setShowConfirmation(false);
    
    try {
      const result = await outlookApiService.sendEmail(
        emailData.to,
        emailData.subject,
        emailData.body,
        { type: 'tenant' }
      );
      
      setSendResult(result);
      
      if (result.success) {
        setTimeout(() => {
          onClose();
          setSendResult(null);
        }, 3000);
      }
    } catch (error) {
      setSendResult({
        success: false,
        error: 'Fehler beim Senden der E-Mail: ' + error.message
      });
    } finally {
      setSending(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center">
            <Mail className="w-6 h-6 mr-3 text-blue-600" />
            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
              E-Mail-Vorschau
            </h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="flex h-[calc(90vh-120px)]">
          {/* Left Side - Email Settings */}
          <div className="w-1/3 p-6 border-r border-gray-200 dark:border-gray-700 overflow-y-auto">
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">
              E-Mail-Einstellungen
            </h3>
            
            <div className="space-y-4">
              {/* To Field */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  An *
                </label>
                <input
                  type="email"
                  value={emailData.to}
                  onChange={(e) => handleInputChange('to', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-100"
                  placeholder="mieter@example.com"
                />
              </div>

              {/* CC Field */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  CC
                </label>
                <input
                  type="email"
                  value={emailData.cc}
                  onChange={(e) => handleInputChange('cc', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-100"
                  placeholder="cc@example.com"
                />
              </div>

              {/* BCC Field */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  BCC
                </label>
                <input
                  type="email"
                  value={emailData.bcc}
                  onChange={(e) => handleInputChange('bcc', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-100"
                  placeholder="bcc@example.com"
                />
              </div>

              {/* Subject Field */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Betreff *
                </label>
                <input
                  type="text"
                  value={emailData.subject}
                  onChange={(e) => handleInputChange('subject', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-100"
                />
              </div>

              {/* Priority */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Priorität
                </label>
                <select
                  value={emailData.priority}
                  onChange={(e) => handleInputChange('priority', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-100"
                >
                  <option value="low">Niedrig</option>
                  <option value="normal">Normal</option>
                  <option value="high">Hoch</option>
                </select>
              </div>

              {/* Send Time */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Versandzeitpunkt
                </label>
                <select
                  value={emailData.sendTime}
                  onChange={(e) => handleInputChange('sendTime', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-100"
                >
                  <option value="now">Sofort senden</option>
                  <option value="scheduled">Geplant (Preview)</option>
                </select>
              </div>

              {/* Attachment Info */}
              <div className="bg-blue-50 dark:bg-blue-900/20 p-3 rounded-md">
                <div className="flex items-center">
                  <Paperclip className="w-4 h-4 mr-2 text-blue-600" />
                  <span className="text-sm text-blue-800 dark:text-blue-200">
                    PDF-Anhang wird automatisch hinzugefügt
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Right Side - Email Preview */}
          <div className="w-2/3 p-6 overflow-y-auto">
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">
              E-Mail-Vorschau
            </h3>
            
            {/* Email Header Preview */}
            <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg mb-4">
              <div className="space-y-2 text-sm">
                <div><strong>An:</strong> {emailData.to || 'Nicht angegeben'}</div>
                {emailData.cc && <div><strong>CC:</strong> {emailData.cc}</div>}
                {emailData.bcc && <div><strong>BCC:</strong> {emailData.bcc}</div>}
                <div><strong>Betreff:</strong> {emailData.subject || 'Kein Betreff'}</div>
                <div><strong>Priorität:</strong> {
                  emailData.priority === 'high' ? 'Hoch' : 
                  emailData.priority === 'low' ? 'Niedrig' : 'Normal'
                }</div>
              </div>
            </div>

            {/* Email Body */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                E-Mail-Text
              </label>
              <textarea
                value={emailData.body}
                onChange={(e) => handleInputChange('body', e.target.value)}
                className="w-full h-96 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-100 font-mono text-sm"
                placeholder="E-Mail-Text..."
              />
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-gray-200 dark:border-gray-700">
          <div className="flex items-center space-x-2">
            {sendResult && (
              <div className={`flex items-center px-3 py-1 rounded-md ${
                sendResult.success 
                  ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-200'
                  : 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-200'
              }`}>
                {sendResult.success ? (
                  <CheckCircle className="w-4 h-4 mr-1" />
                ) : (
                  <AlertCircle className="w-4 h-4 mr-1" />
                )}
                <span className="text-sm">
                  {sendResult.success ? 'E-Mail erfolgreich gesendet!' : sendResult.error}
                </span>
              </div>
            )}
          </div>
          
          <div className="flex space-x-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              Abbrechen
            </button>
            <button
              onClick={handleSendEmail}
              disabled={sending || !emailData.to.trim() || !emailData.subject.trim()}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
            >
              {sending ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Wird gesendet...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  E-Mail senden
                </>
              )}
            </button>
          </div>
        </div>

        {/* Confirmation Dialog */}
        {showConfirmation && (
          <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center">
            <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-xl max-w-md w-full mx-4">
              <div className="flex items-center mb-4">
                <AlertCircle className="w-6 h-6 text-orange-500 mr-3" />
                <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">
                  E-Mail senden bestätigen
                </h3>
              </div>
              <p className="text-gray-600 dark:text-gray-400 mb-6">
                Sind Sie sicher, dass Sie die E-Mail an <strong>{emailData.to}</strong> senden möchten?
              </p>
              <div className="flex space-x-3">
                <button
                  onClick={() => setShowConfirmation(false)}
                  className="flex-1 px-4 py-2 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700"
                >
                  Abbrechen
                </button>
                <button
                  onClick={confirmSendEmail}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                  Ja, senden
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EmailPreviewModal;

